/**
 * Category landing pages.
 *
 * Category browsing used to exist only as `/shop?category=dhoop-sticks`. A
 * query parameter on a shared page can't carry its own title, description or
 * copy, wasn't in the sitemap, and Google treats parameterised filter views as
 * near-duplicates of the page they filter — so the four categories had no
 * indexable presence at all.
 *
 * That matters because category terms are where the commercial search volume
 * actually is. "dhoop sticks online" and "sambrani cup price" are searched far
 * more than any single fragrance name, and those searches want a page about the
 * category, not a product page and not an unfiltered catalogue.
 *
 * Each entry below is a real page at its own URL. `intro` and `faqs` exist so
 * the page says something a filtered grid doesn't — without that it is a thin
 * page, which ranks no better than the query parameter did.
 *
 * `slug` must match the `category` field on products in Data.js.
 */

export const CATEGORY_PAGES = [
  {
    slug: "incense-sticks",
    name: "Incense Sticks",
    path: "/incense-sticks",
    // Share image. Without it a category link previews as the bare logo,
    // which tells someone scrolling WhatsApp nothing about what was sent.
    image: "/assets/nt/isticks.jpg",
    h1: "Incense Sticks (Agarbatti) — Made in Our Indore Factory",
    title: "Incense Sticks Online — Natural Agarbatti from ₹55 | NT Sticks",
    description:
      "Buy natural incense sticks direct from our Indore factory. Charcoal-free, low-smoke agarbatti in 23 fragrances — zipper packs, boxes and jars. Free shipping above ₹200.",
    intro:
      "Our incense sticks are rolled on a thin bamboo core and hand-dipped in perfume oils, the format most Indian households know as agarbatti. They light quickly, burn for roughly 30 minutes and carry fragrance further than dhoop, which makes them the practical choice for larger rooms, entrances and daily morning puja. Every batch is made at our own factory in Indore — we mix, roll and pack in-house, so there is no third-party blending between the raw material and the pouch you open.",
    faqs: [
      {
        q: "What is the difference between incense sticks and dhoop sticks?",
        a: "Incense sticks (agarbatti) are rolled around a thin bamboo core, so a bare stick remains after burning. Dhoop sticks have no bamboo — the whole stick is fragrant paste, so it burns completely and gives a denser, closer aroma with no bamboo smoke.",
      },
      {
        q: "How long does one incense stick burn?",
        a: "Around 30 minutes for our standard length, depending on airflow. In a draught it burns faster; in a closed room it lasts longer.",
      },
      {
        q: "Are your incense sticks charcoal-free?",
        a: "Yes. We use natural binding material rather than charcoal powder, which is what keeps the smoke light and the ash pale instead of black.",
      },
    ],
  },
  {
    slug: "dhoop-sticks",
    name: "Dhoop Sticks",
    path: "/dhoop-sticks",
    // Share image. Without it a category link previews as the bare logo,
    // which tells someone scrolling WhatsApp nothing about what was sent.
    image: "/assets/nt/sticks.jpg",
    h1: "Dhoop Sticks — Bamboo-Free, Charcoal-Free, Factory Direct",
    title: "Dhoop Sticks Online — Bamboo-Free Dhoop Batti from ₹55 | NT Sticks",
    description:
      "Buy dhoop sticks direct from the Indore factory that makes them. Bamboo-free, charcoal-free, low-smoke dhoop batti in sandal, mogra, loban, guggal and 20 more fragrances.",
    intro:
      "A dhoop stick is solid fragrant paste with no bamboo core, so it burns away completely and leaves no stick behind. Without bamboo there is no sharp woodsmoke underneath the perfume, which is why dhoop reads as rounder and more resinous than agarbatti and why it suits small puja rooms, meditation corners and anywhere the smoke sits close. Ours are pressed for a slow, even burn and sealed in resealable jars so the fragrance does not flatten between uses.",
    faqs: [
      {
        q: "Why choose dhoop sticks over agarbatti?",
        a: "No bamboo core means no bamboo smoke, so the fragrance comes through cleaner and the stick burns away completely. Dhoop also gives a denser aroma in a smaller space, which suits enclosed puja rooms better than agarbatti.",
      },
      {
        q: "Do dhoop sticks produce a lot of smoke?",
        a: "Ours are made without charcoal, so the smoke is noticeably lighter than charcoal-based dhoop and the ash is pale rather than black.",
      },
      {
        q: "How long does a dhoop stick burn?",
        a: "About 25 minutes, depending on airflow. In a draught it burns faster; in a closed room it lasts a little longer. Because there is no bamboo core the stick burns away completely, leaving only fine ash.",
      },
      {
        q: "How should dhoop sticks be stored?",
        a: "Keep them sealed in the jar or pouch, away from direct sunlight and damp. Stored that way the fragrance holds for well over a year.",
      },
    ],
  },
  {
    slug: "dhoop-cone",
    name: "Dhoop Cones",
    path: "/dhoop-cones",
    // Share image. Without it a category link previews as the bare logo,
    // which tells someone scrolling WhatsApp nothing about what was sent.
    image: "/assets/nt/cone-sandal.jpg",
    h1: "Dhoop Cones — Slow-Burning, Bamboo-Free",
    title: "Dhoop Cones Online — Natural Sambrani Cones | NT Sticks",
    description:
      "Natural dhoop cones made at our Indore factory. Bamboo-free, charcoal-free and slow-burning, in jars and boxes. Ideal for puja rooms and short rituals.",
    intro:
      "Dhoop cones hold the same fragrant paste as our dhoop sticks in a denser, self-standing shape. A cone burns from the tip downward and releases fragrance more intensely than a stick of the same weight, which makes it well suited to short, concentrated rituals and to rooms where a stick would be awkward to stand. Ours come in a resealable 100g jar, bamboo-free and charcoal-free like the rest of our range, across nine fragrances — from sandal, rose and mogra to lavender, amber and pineapple. Burn them on a metal plate or a ceramic holder — never directly on a surface that marks with heat.",
    faqs: [
      {
        q: "How is a dhoop cone different from a dhoop stick?",
        a: "Same paste, denser shape. A cone concentrates the same material into a smaller footprint, so it releases fragrance faster and more intensely than a stick of equal weight — better for a short ritual, where a stick suits a longer sitting.",
      },
      {
        q: "How long does a dhoop cone burn?",
        a: "About 25 minutes, burning from the tip downward. A cone holds the same paste as a stick in a denser shape, so it gives up its fragrance more intensely over that time rather than lasting longer.",
      },
      {
        q: "What surface should I burn a dhoop cone on?",
        a: "A metal plate, a ceramic holder or a bed of ash. The base of a cone gets hot enough to mark wood and laminate.",
      },
    ],
  },
  {
    slug: "sambrani-cup",
    name: "Sambrani & Havan Cups",
    path: "/sambrani-cups",
    // Share image. Without it a category link previews as the bare logo,
    // which tells someone scrolling WhatsApp nothing about what was sent.
    image: "/assets/nt/cups.jpg",
    h1: "Sambrani Cups & Havan Cups — Ready to Light, No Charcoal Needed",
    title: "Sambrani Cups & Havan Cups Online — Ready-to-Light | NT Sticks",
    description:
      "Ready-to-light sambrani cups and havan cups from our Indore factory. No separate charcoal or kandi needed — light the cup and it burns on its own. Boxes of 10, 12 and jars of 25.",
    intro:
      "A sambrani cup packs benzoin resin and natural fragrance into a cup that lights on its own — no loose charcoal, no kandi, no tongs. Touch a flame to the rim, wait a few seconds, and it catches, giving the thick, sweet-resinous smoke traditionally used for havan, for drying and scenting hair, and for clearing a room before prayer. It is the cleanest way to get sambrani smoke without handling burning charcoal. We make them in two materials — charcoal-based black cups, and cow dung cups for a softer, earthier smoke — twelve to a box.",
    faqs: [
      {
        q: "Do I need charcoal to light a sambrani cup?",
        a: "No. The cup is self-igniting — hold a flame to the rim for a few seconds and it catches on its own. That is the main advantage over loose sambrani, which needs a lit charcoal disc underneath.",
      },
      {
        q: "Do sambrani cups need a stand?",
        a: "Yes, always. The cup burns through completely and its base gets hot enough to scorch whatever it is standing on, so set it on a stand or a metal plate before lighting. Keep it clear of anything flammable, and out of reach of children while it burns — these cups give off a lot of smoke, so light one at a time in a room with some air.",
      },
      {
        q: "How long does a sambrani cup burn?",
        a: "About 25 minutes. The cup burns through completely and settles into ash, so one cup is usually enough for a room or for a single havan.",
      },
      {
        q: "What is the difference between the black cups and the cow dung cups?",
        a: "The black cups are charcoal-based and burn with the stronger, more resinous smoke most people associate with sambrani. The cow dung cups use the material traditionally used for havan and burn softer and earthier, which suits daily use where the black cups can feel heavy.",
      },
      {
        q: "What is the difference between a sambrani cup and a havan cup?",
        a: "They are the same format. 'Havan cup' describes the use — burning during havan or hom — while 'sambrani cup' names the benzoin resin it is made from.",
      },
    ],
  },
];

export const CATEGORY_BY_SLUG = Object.fromEntries(
  CATEGORY_PAGES.map((c) => [c.slug, c])
);

export const CATEGORY_BY_PATH = Object.fromEntries(
  CATEGORY_PAGES.map((c) => [c.path, c])
);
